import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import Loading from 'components/blocks/Loading';
import isEqual from 'lodash/isEqual';
import { Row, Col, Modal } from 'components/ui';
import PermissionsSelector from './PermissionsSelector';
import Joi from 'joi';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { getUserDetails } from '../../../store/selectors/auth';
import UserRoleSelector from './UserRoleSelector';
import { Avatar } from 'antd';
import { user as userImage } from '../../../assets/img';
import ProjectSelector from './ProjectSelector';
import { getProjectsFromServer } from 'store/actions/projects';

const validationSchema = Joi.object().keys({
  role: Joi.string().allow('admin', 'regular', 'custom'),
  permissions: Joi.when('role', {
    is: 'custom',
    then: Joi.array()
      .items(Joi.string())
      .min(1),
    otherwise: Joi.forbidden(),
  }),
  project: Joi.number().allow(''),
});

const extractErrorMessageByKey = (key, errors = []) => {
  return errors.find(e => e.path[0] === key)?.message;
};

class AddUserModal extends Component {
  state = {
    role: '',
    project: '',
    errors: {},
    permissions: [],
  };

  permissionSelector = React.createRef();

  componentDidMount() {
    this.getStateFromProps();
    this.props.onGetProjects();
  }

  componentDidUpdate(prevProps) {
    this.comparePrevUserPropsToCurrentProps(prevProps);
    this.resetFormWhenAddingUser(prevProps);
  }

  comparePrevUserPropsToCurrentProps = prevProps => {
    const { user: prevUser, isVisible: prevIsVisible } = prevProps;
    const { user, isVisible } = this.props;
    if (
      !isEqual(prevUser, user) ||
      (prevIsVisible !== isVisible && isVisible)
    ) {
      this.getStateFromProps();
    }
  };

  resetFormWhenAddingUser = prevProps => {
    const { isVisible, user } = this.props;
    const isOpening = !prevProps.isVisible && isVisible;
    const hasId = user.id || user.id === 0;
    if (isOpening && !hasId) this.resetForm();
  };

  getStateFromProps = () => {
    const { user } = this.props;
    const { roles } = user;
    this.setState({ role: roles[0]?.name, project: user?.project?.id || '' });
  };

  clearError = key => ({ ...this.state.errors, [key]: '' });

  handleSaveUser = () => {
    const { id } = this.props.user;
    const { role, project } = this.state;
    const values = { role, project };
    if (role === 'custom') {
      values.permissions = this.permissionSelector.current?.getSelectedPermissions();
    }
    if (this.validateValues(values)) {
      this.props.onSaveUser({ ...values, user_id: id });
    }
  };

  validateValues = values => {
    const { error } = validationSchema.validate(values, { abortEarly: false });
    if (error) {
      this.displayErrors(error.details, values);
      return false;
    }
    return true;
  };

  displayErrors = (validationErrors, values) => {
    const errors = {};
    Object.keys(values).forEach(key => {
      if (extractErrorMessageByKey(key, validationErrors)) {
        errors[key] = extractErrorMessageByKey(key, validationErrors);
      }
    });
    this.setState({ errors });
  };

  handleClose = () => {
    this.props.onHide();
    this.resetForm();
  };

  handleSelectedRole = role => {
    this.setState({ role });
  };

  handleSelectProject = project => {
    this.setState({ project });
  };

  resetForm = () => {
    this.setState({
      role: '',
      project: '',
      errors: {},
    });
  };

  render() {
    const { isVisible, isLoading, user } = this.props;
    const { errors, role, project } = this.state;

    const { avatar, fullName, userName, email } = user;

    return (
      <Modal
        height="50vh"
        title={
          <div className="flex items-center space-x-4">
            <Avatar src={avatar || userImage} />
            <p style={{ marginBottom: 0 }}>
              {fullName || userName} ({email})
            </p>
          </div>
        }
        visible={isVisible}
        width="80vw"
        onCancel={this.handleClose}
        onOk={this.handleSaveUser}
      >
        {isLoading && <Loading />}
        <div className="user-editor">
          <Row gutter={8}>
            <Col span={12}>
              <Fragment>
                <span>User Role</span>
              </Fragment>
              <UserRoleSelector
                errorTxt={errors.role}
                role={role}
                userId={this.props.user?.id}
                onRoleChange={this.handleSelectedRole}
              />
            </Col>
            <Col span={12}>
              <span>Project</span>
              <ProjectSelector
                selectedProject={project}
                onChangeProject={this.handleSelectProject}
              />
            </Col>
          </Row>
          <Row>
            <PermissionsSelector
              ref={this.permissionSelector}
              role={this.state.role}
              userId={this.props.user?.id}
            />
          </Row>
        </div>
      </Modal>
    );
  }
}

const userShape = PropTypes.shape({
  id: PropTypes.number,
  name: PropTypes.string,
  email: PropTypes.string,
  password: PropTypes.string,
  role: PropTypes.string,
});

AddUserModal.defaultProps = {
  isLoading: false,
  user: {},
};

AddUserModal.propTypes = {
  isLoading: PropTypes.bool,
  isVisible: PropTypes.bool.isRequired,
  user: userShape,
  onHide: PropTypes.func.isRequired,
  onSaveUser: PropTypes.func.isRequired,
};

const selector = createStructuredSelector({
  currentUser: getUserDetails,
});

export default connect(
  selector,
  { onGetProjects: getProjectsFromServer },
)(AddUserModal);
